import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Toolbar menu that inserts a patient/clinic value at the cursor position.
export default function InsertPatientDataMenu({ editor, groups, disabled, isLoading, hasError }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const insertValue = (value) => {
    setAnchorEl(null);
    // Inserted as a text node so values are never parsed as HTML.
    editor.chain().focus().insertContent({ type: 'text', text: value }).run();
  };

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<PersonAddAltOutlinedIcon fontSize="small" />}
        endIcon={<ArrowDropDownIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled}
        aria-haspopup="true"
        sx={{ fontSize: '0.85rem' }}
      >
        Inserir dado
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 320 } } }}
      >
        {(isLoading || hasError) && (
          <Typography
            variant="caption"
            color={hasError ? 'error' : 'text.secondary'}
            sx={{ display: 'block', px: 2, pt: 1, pb: 0.5 }}
          >
            {hasError
              ? 'Não foi possível carregar os dados do paciente.'
              : 'Carregando dados do paciente...'}
          </Typography>
        )}
        {groups.map((group, index) => [
          index > 0 && <Divider key={`${group.key}-divider`} />,
          <ListSubheader
            key={`${group.key}-header`}
            sx={{ lineHeight: '32px', fontWeight: 'bold' }}
          >
            {group.label}
          </ListSubheader>,
          ...group.fields.map((field) => {
            const hasValue = Boolean(field.value && String(field.value).trim());
            return (
              <MenuItem
                key={field.key}
                disabled={!hasValue}
                onClick={() => insertValue(String(field.value))}
                dense
              >
                <ListItemText
                  primary={field.label}
                  secondary={hasValue ? field.value : 'não cadastrado'}
                  slotProps={{
                    secondary: {
                      sx: { fontStyle: hasValue ? 'normal' : 'italic' },
                      noWrap: true,
                    },
                  }}
                  sx={{ maxWidth: 360 }}
                />
              </MenuItem>
            );
          }),
        ])}
      </Menu>
    </>
  );
}

InsertPatientDataMenu.propTypes = {
  editor: PropTypes.object.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          value: PropTypes.string,
        }),
      ).isRequired,
    }),
  ).isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
};

InsertPatientDataMenu.defaultProps = {
  disabled: false,
  isLoading: false,
  hasError: false,
};
